import { BackgroundColors } from '../../../colors/backgroundColors/backgroundColors/index.js';
import { CornerRadius } from '../../../cornerRadius/cornerRadius/index.js';

/**
 * Generates a set of styles for custom scrollbars, with support for WebKit-based browsers,
 * Mozilla's scrollbar properties, and coarse pointer devices.
 *
 * @param options - Configuration options of type `ScrollbarStylesType` for customizing the scrollbar styles.
 *   - `width` (optional): The width of the scrollbar, defaults to `4px`.
 *   - `height` (optional): The height of the scrollbar.
 *   - `scrollbarProps` (optional): Additional CSS properties for the `::-webkit-scrollbar` pseudo-element.
 *   - `trackProps` (optional): Additional CSS properties for the `::-webkit-scrollbar-track` pseudo-element.
 *   - `thumbProps` (optional): Additional CSS properties for the `::-webkit-scrollbar-thumb` pseudo-element.
 * @returns An object containing the generated CSS styles for scrollbars.
 *
 * ### Example
 * ```typescript
 * import { scrollbar } from './scrollbar';
 *
 * const styles = scrollbar({
 *   width: 6,
 *   scrollbarProps: { backgroundColor: '#f0f0f0' },
 *   trackProps: { backgroundColor: '#ddd' },
 *   thumbProps: { backgroundColor: '#aaa' },
 * });
 *
 * console.log(styles);
 * // {
 * //   '@media (pointer: fine)': {
 * //     '&::-webkit-scrollbar': {
 * //       width: 6,
 * //       height: undefined,
 * //       backgroundColor: '#f0f0f0',
 * //     },
 * //     '&::-webkit-scrollbar-track': {
 * //       borderRadius: '4px',
 * //       backgroundColor: 'transparent',
 * //     },
 * //     '&::-webkit-scrollbar-thumb': {
 * //       borderRadius: '4px',
 * //       backgroundColor: '#aaa',
 * //     },
 * //   },
 * //   '@supports (-moz-appearance:none)': {
 * //     '&': {
 * //       scrollbarColor: '#aaa transparent',
 * //       scrollbarWidth: 'thin',
 * //     },
 * //   },
 * //   '@media (pointer: coarse)': {
 * //     '::-webkit-scrollbar': {
 * //       display: 'none',
 * //     },
 * //   },
 * // }
 * ```
 *
 * ### Key Features:
 * - **WebKit Scrollbars**:
 *   - Styles for `::-webkit-scrollbar`, `::-webkit-scrollbar-track`, and `::-webkit-scrollbar-thumb`.
 * - **Mozilla Scrollbars**:
 *   - Supports `scrollbarColor` and `scrollbarWidth` properties.
 * - **Coarse Pointer Devices**:
 *   - Hides scrollbars on devices with coarse pointers.
 */
const scrollbar = ({ width = 4, height, scrollbarProps, trackProps, thumbProps, } = {}) => ({
    '@media (pointer: fine)': {
        '&::-webkit-scrollbar': {
            width,
            height,
            ...scrollbarProps,
        },
        '&::-webkit-scrollbar-track': {
            borderRadius: CornerRadius.r4,
            backgroundColor: 'transparent',
            ...trackProps,
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: CornerRadius.r4,
            backgroundColor: BackgroundColors.border,
            ...thumbProps,
        },
        '&::-webkit-scrollbar-corner': {
            background: 'transparent',
        },
    },
    '@supports (-moz-appearance:none)': {
        '&': {
            scrollbarColor: `${BackgroundColors.border} transparent`,
            scrollbarWidth: 'thin',
        },
    },
    '@media (pointer: coarse)': {
        '::-webkit-scrollbar': {
            display: 'none',
        },
    },
});

export { scrollbar };
